import type { UserGuideSection } from './types';
import userGuideMarkdown from './content/user-guide.md?raw';

type ParsedSection = UserGuideSection & { level: 2 | 3 | 4 };

function parseHeading(line: string): Omit<ParsedSection, 'content' | 'subsections'> & { level: 2 | 3 | 4 } | null {
  const match = /^(#{2,4})\s+(.+?)\s+\{#([a-z0-9-]+)\}\s*$/.exec(line.trim());
  if (!match) return null;

  const rawTitle = match[2].trim();
  const numberMatch = /^(\d+(?:\.\d+)*)\s+(.+)$/.exec(rawTitle);

  return {
    id: match[3],
    number: numberMatch?.[1],
    title: numberMatch?.[2] ?? rawTitle,
    level: match[1].length as 2 | 3 | 4,
  };
}

function buildSectionTree(flat: ParsedSection[]): UserGuideSection[] {
  const roots: ParsedSection[] = [];
  const level3Stack: ParsedSection[] = [];

  for (const section of flat) {
    if (section.level === 2) {
      roots.push(section);
      level3Stack.length = 0;
      continue;
    }

    if (section.level === 3) {
      const parent = roots.at(-1);
      if (!parent) continue;
      parent.subsections = [...(parent.subsections ?? []), section];
      level3Stack.push(section);
      continue;
    }

    const parent = level3Stack.at(-1) ?? roots.at(-1);
    if (!parent) continue;
    parent.subsections = [...(parent.subsections ?? []), section];
  }

  const stripLevel = ({ level: _level, ...section }: ParsedSection): UserGuideSection => ({
    id: section.id,
    number: section.number,
    title: section.title,
    content: section.content,
    subsections: section.subsections?.map((sub) => stripLevel(sub as ParsedSection)),
  });

  return roots.map(stripLevel);
}

function parseSections(markdown: string): UserGuideSection[] {
  const flat: ParsedSection[] = [];
  let current: ParsedSection | null = null;
  let contentLines: string[] = [];

  const flush = () => {
    if (!current) return;
    flat.push({ ...current, content: contentLines.join('\n').trim() });
    current = null;
    contentLines = [];
  };

  for (const line of markdown.split('\n')) {
    const heading = parseHeading(line);
    if (heading) {
      flush();
      current = { ...heading, content: '', subsections: [] };
      continue;
    }
    if (current) contentLines.push(line);
  }

  flush();
  return buildSectionTree(flat);
}

function flattenSections(sections: UserGuideSection[]): UserGuideSection[] {
  const result: UserGuideSection[] = [];
  for (const section of sections) {
    result.push(section);
    if (section.subsections) result.push(...flattenSections(section.subsections));
  }
  return result;
}

export const userGuideSections = parseSections(userGuideMarkdown);

export const tableOfContents = flattenSections(userGuideSections);
